import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Plus, ArrowLeft, Send, LifeBuoy, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageLoading, EmptyState, fmtDateTime } from "@/components/shared";
import api, { formatApiErrorDetail } from "@/lib/api";

export const CATS = ["GENERAL", "BILLING", "TECHNICAL", "COMPLIANCE", "FEATURE_REQUEST", "OTHER"];
export const PRIS = ["LOW", "NORMAL", "HIGH", "URGENT"];
export const catKey = { GENERAL: "catGeneral", BILLING: "catBilling", TECHNICAL: "catTechnical", COMPLIANCE: "catCompliance", FEATURE_REQUEST: "catFeature", OTHER: "catOther" };
export const priKey = { LOW: "priLow", NORMAL: "priNormal", HIGH: "priHigh", URGENT: "priUrgent" };
const stKey = { OPEN: "stOpen", IN_PROGRESS: "stInProgress", WAITING: "stWaiting", RESOLVED: "stResolved", CLOSED: "stClosed" };
const STATUS_STYLE = { OPEN: "bg-blue-100 text-blue-700", IN_PROGRESS: "bg-amber-100 text-amber-700", WAITING: "bg-purple-100 text-purple-700", RESOLVED: "bg-emerald-100 text-emerald-700", CLOSED: "bg-slate-200 text-slate-600" };
const PRI_STYLE = { LOW: "text-slate-400", NORMAL: "text-slate-500", HIGH: "text-amber-600", URGENT: "text-red-600" };

export function StatusBadge({ status }) {
  const { t } = useTranslation();
  return <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${STATUS_STYLE[status] || "bg-slate-100 text-slate-600"}`}>{t(`sup.${stKey[status] || "stOpen"}`)}</span>;
}

export function AttachmentChips({ ticketId, attachments }) {
  const dl = async (a) => {
    try {
      const r = await api.get(`/support/tickets/${ticketId}/attachments/${a.id}/download`, { responseType: "blob" });
      window.open(URL.createObjectURL(r.data), "_blank");
    } catch { toast.error("Download failed"); }
  };
  if (!attachments?.length) return null;
  return (
    <div className="mt-2 flex flex-wrap gap-2">
      {attachments.map((a) => (
        <button key={a.id} onClick={() => dl(a)} className="inline-flex items-center gap-1 rounded-lg bg-black/10 px-2 py-1 text-xs hover:bg-black/20" data-testid={`attachment-${a.id}`}>
          <Download className="h-3 w-3" /> {a.filename}
        </button>
      ))}
    </div>
  );
}

export function AttachUploader({ value, onChange, testid }) {
  const { t } = useTranslation();
  const [up, setUp] = useState(false);
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUp(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const r = await api.post("/support/attachments", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange([...(value || []), r.data]);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setUp(false); e.target.value = ""; }
  };
  return (
    <div>
      <label className="text-xs text-slate-400">{t("sup.attach")} <span className="opacity-70">({t("sup.attachHint")})</span></label>
      <input type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" onChange={onFile} data-testid={testid} className="mt-1 block w-full text-xs" />
      {up && <span className="text-xs text-slate-400">…</span>}
      {(value || []).length > 0 && <div className="mt-1 flex flex-wrap gap-1">{value.map((a, i) => <span key={i} className="rounded bg-slate-100 px-2 py-0.5 text-[11px] text-slate-600">{a.filename}</span>)}</div>}
    </div>
  );
}

export function Thread({ messages, ticketId }) {
  return (
    <div className="space-y-3" data-testid="ticket-thread">
      {(messages || []).map((m) => (
        <div key={m.id} className={`flex ${m.author_type === "admin" ? "justify-start" : "justify-end"}`}>
          <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 ${m.author_type === "admin" ? "bg-navy text-white" : "bg-slate-100 text-navy"}`}>
            <div className="mb-0.5 text-[11px] font-semibold opacity-70">{m.author_type === "admin" ? "EmployGuard Support" : m.author_name}</div>
            {m.body && <div className="whitespace-pre-wrap text-sm">{m.body}</div>}
            <AttachmentChips ticketId={ticketId} attachments={m.attachments} />
            <div className="mt-1 text-[10px] opacity-50">{fmtDateTime(m.created_at)}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function SupportPage() {
  const { t } = useTranslation();
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ subject: "", category: "GENERAL", priority: "NORMAL", message: "" });
  const [atts, setAtts] = useState([]);
  const [replyAtts, setReplyAtts] = useState([]);
  const [active, setActive] = useState(null);
  const [reply, setReply] = useState("");

  const load = () => api.get("/support/tickets").then((r) => setRows(r.data.tickets));
  useEffect(() => { load(); }, []);
  const openTicket = (id) => api.get(`/support/tickets/${id}`).then((r) => setActive(r.data.ticket));

  const create = async () => {
    if (!form.subject.trim() || !form.message.trim()) { toast.error(t("sup.reqFields")); return; }
    setBusy(true);
    try {
      await api.post("/support/tickets", { ...form, attachments: atts });
      toast.success(t("sup.ticketCreated"));
      setOpen(false); setForm({ subject: "", category: "GENERAL", priority: "NORMAL", message: "" }); setAtts([]); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const sendReply = async () => {
    if (!reply.trim()) return;
    try { await api.post(`/support/tickets/${active.id}/reply`, { message: reply, attachments: replyAtts }); setReply(""); setReplyAtts([]); toast.success(t("sup.replySent")); openTicket(active.id); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (rows === null) return <PageLoading />;

  if (active) {
    return (
      <div data-testid="support-ticket-detail">
        <button onClick={() => setActive(null)} className="mb-4 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-navy" data-testid="support-back"><ArrowLeft className="h-4 w-4" /> {t("sup.back")}</button>
        <div className="rounded-2xl border border-slate-200 bg-white p-6">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 pb-4">
            <div>
              <div className="font-heading text-lg font-bold text-navy">{active.subject}</div>
              <div className="mt-1 text-xs text-slate-400">{t(`sup.${catKey[active.category]}`)} · <span className={PRI_STYLE[active.priority]}>{t(`sup.${priKey[active.priority]}`)}</span></div>
            </div>
            <StatusBadge status={active.status} />
          </div>
          <Thread messages={active.messages} ticketId={active.id} />
          {active.status !== "CLOSED" ? (
            <div className="mt-5 space-y-2">
              <div className="flex items-end gap-2">
                <Textarea value={reply} onChange={(e) => setReply(e.target.value)} rows={2} placeholder={t("sup.replyPlaceholder")} data-testid="support-reply-input" />
                <Button onClick={sendReply} className="rounded-full bg-cyan text-white hover:bg-cyan-dark" data-testid="support-reply-send"><Send className="h-4 w-4" /></Button>
              </div>
              <AttachUploader value={replyAtts} onChange={setReplyAtts} testid="support-reply-attach" />
            </div>
          ) : <div className="mt-5 rounded-xl bg-slate-50 px-4 py-3 text-xs text-slate-500">{t("sup.closedNote")}</div>}
        </div>
      </div>
    );
  }

  return (
    <div data-testid="support-page">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-extrabold text-navy sm:text-3xl">{t("sup.title")}</h1>
          <p className="mt-1 text-sm text-slate-500">{t("sup.subtitle")}</p>
        </div>
        <Button onClick={() => setOpen(true)} className="rounded-full bg-navy text-white hover:bg-navy-muted" data-testid="support-new-btn"><Plus className="mr-1 h-4 w-4" /> {t("sup.newTicket")}</Button>
      </div>

      {rows.length === 0 ? (
        <EmptyState icon={LifeBuoy} title={t("sup.noTickets")} description={t("sup.noTicketsDesc")} />
      ) : (
        <div className="space-y-2" data-testid="support-ticket-list">
          {rows.map((tk) => (
            <button key={tk.id} onClick={() => openTicket(tk.id)} data-testid={`support-ticket-${tk.id}`}
              className="flex w-full items-center justify-between rounded-2xl border border-slate-200 bg-white px-5 py-4 text-left transition-colors hover:border-cyan/40 hover:bg-cyan/5">
              <div className="min-w-0">
                <div className="truncate font-semibold text-navy">{tk.subject}</div>
                <div className="mt-0.5 text-xs text-slate-400">{t(`sup.${catKey[tk.category]}`)} · {fmtDateTime(tk.updated_at)}</div>
              </div>
              <StatusBadge status={tk.status} />
            </button>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="support-new-dialog">
          <DialogHeader><DialogTitle>{t("sup.newTicket")}</DialogTitle></DialogHeader>
          <div className="space-y-2"><Label>{t("sup.subject")}</Label><Input value={form.subject} onChange={(e) => setForm((s) => ({ ...s, subject: e.target.value }))} data-testid="support-subject-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2"><Label>{t("sup.category")}</Label>
              <Select value={form.category} onValueChange={(v) => setForm((s) => ({ ...s, category: v }))}>
                <SelectTrigger data-testid="support-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>{CATS.map((c) => <SelectItem key={c} value={c}>{t(`sup.${catKey[c]}`)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>{t("sup.priority")}</Label>
              <Select value={form.priority} onValueChange={(v) => setForm((s) => ({ ...s, priority: v }))}>
                <SelectTrigger data-testid="support-priority-select"><SelectValue /></SelectTrigger>
                <SelectContent>{PRIS.map((p) => <SelectItem key={p} value={p}>{t(`sup.${priKey[p]}`)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2"><Label>{t("sup.message")}</Label><Textarea rows={5} value={form.message} onChange={(e) => setForm((s) => ({ ...s, message: e.target.value }))} data-testid="support-message-input" /></div>
          <AttachUploader value={atts} onChange={setAtts} testid="support-attach" />
          <DialogFooter><Button onClick={create} disabled={busy} className="rounded-full bg-navy text-white hover:bg-navy-muted" data-testid="support-submit-btn">{t("sup.send")}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
